import _ from 'lodash';
import BaselineGrid from '../grid';
import moment from '../../../../libs/moment';
import constants from '$$helpers/constants';

const PLANNED_START_FIELD = 'planned_start';
const PLANNED_END_FIELD = 'planned_end';

class BaselineChart {
  constructor() {
    this.instance = null;
    this.layersIds = {};
    this.helpers = {
      parseDate: date => this.instance.date.parseDate(moment(date).format('YYYY-MM-DD H:mm:ss'), '%Y/%m/%d %H:%i:%s'),
      addClass(selector, cssClass) {
        const element = document.querySelector(selector);

        element && element.classList.add(cssClass);
      },
      removeClass(selector, cssClass) {
        const element = document.querySelector(selector);

        element && element.classList.remove(cssClass);
      },
    };
  }

  init(instance) {
    this.instance = instance;
    this.clearBaselines(true);
  }

  onBeforeGanttRender() {
    if (!this.instance) {
      return;
    }

    const baselines = this.instance.ganttDataApi.getUserActivitySettings(constants.USER_ACTIVITIES.BASELINES);

    if (baselines && !baselines[this.instance.config.gantt_id]) {
      this.instance.config.baseline = 0;
    }

    if (this.instance.config.baseline === 0) {
      this.instance.config.row_height = this.instance.config.original_row_height;
      this.helpers.removeClass('.dhx_gantt_container', 'baseline-container');
    }
  }

  clearBaselines(removeClass) {
    this.instance.config.baseline = 0;

    _.each(this.layersIds, layerID => {
      layerID && !_.isUndefined(layerID) && this.instance.removeTaskLayer(layerID);
    });

    this.layersIds = {};

    if (removeClass) {
      this.helpers.removeClass('.dhx_gantt_container', 'baseline-container');
    }
  }

  updateTask(baselineId, baselineTask, projectBaselineData) {
    const task = this.instance.isTaskExists(baselineTask.id) && this.instance.getTask(baselineTask.id);

    if (task && task.start_date && task.end_date && task.parent !== 0) {
      baselineTask.start_date = this.helpers.parseDate(baselineTask.start_date);
      baselineTask.end_date = this.instance.calculateEndDate({
        start_date: baselineTask.start_date,
        duration: baselineTask.duration,
        baselineTask,
      });

      const copy = this.instance.__scaleTask(baselineTask);

      BaselineGrid.addBaselineTask(baselineTask, projectBaselineData);

      task[PLANNED_START_FIELD + baselineId] = copy.start_date;

      if (new Date(baselineTask.end_date).valueOf() === new Date(baselineTask.start_date).valueOf()
        && baselineTask.duration > 0) {
        task[PLANNED_END_FIELD + baselineId] = this.instance.calculateEndDate(copy.start_date, copy.duration);
      } else {
        task[PLANNED_END_FIELD + baselineId] = copy.end_date;
      }
    }
  }

  addTaskLayer(baseline, task) {
    const baselineId = baseline.id;

    if (task[PLANNED_START_FIELD + baselineId] && task[PLANNED_END_FIELD + baselineId]) {
      const sizes = this.instance.getTaskPosition(task,
        this.helpers.parseDate(task[PLANNED_START_FIELD + baselineId]),
        this.helpers.parseDate(task[PLANNED_END_FIELD + baselineId]));

      const right = this.instance.templates.rightside_text(
        task[PLANNED_START_FIELD + baselineId],
        task[PLANNED_END_FIELD + baselineId], {
          text: baseline.name,
          start_date: task[PLANNED_START_FIELD + baselineId],
          end_date: task[PLANNED_END_FIELD + baselineId],
        },
      );

      const element = document.createElement('div');
      const baselineNameNode = document.createElement('div');

      baselineNameNode.className = `baseline_name ${right ? 'gantt_side_content gantt_right' : 'gantt_task_content'}`;
      baselineNameNode.innerHTML = '';// baselineModel.getName(baselineId);

      if (this.instance.config.baseline === 2) {
        baselineNameNode.style['font-size'] = `${this.instance.config.bar_height / (this.instance.config.baseline) - 2}px`;
        if (!right) {
          baselineNameNode.style['letter-spacing'] = `${2}px`;
        }
      }

      element.className = `baseline baseline-${baselineId}`;
      element.style.width = `${sizes.width}px`;
      element.style.left = `${sizes.left}px`;
      element.style.height = '6px';
      element.style['line-height'] = '10px';
      if (task.type === 'project') {
        element.className = 'baseline project-baseline';
      }

      let topPosition = `${sizes.top + (this.instance.config.bar_height + 5)}px`;

      if (task.type === this.instance.config.types.project) topPosition = `${sizes.top + (this.instance.config.bar_height + 19)}px`;

      element.style.top = topPosition;

      element.appendChild(baselineNameNode);

      const baselineContainer = document.querySelector('.baseline-container');

      if (baselineContainer && baselineContainer.classList.contains('touch')) {
        element.style.top = `${sizes.top + (task.type !== this.instance.config.types.milestone ? (this.instance.config.bar_height + 4) : (this.instance.config.bar_height + parseInt(element.style.height, 10) + 4))}px`;
      }

      if (baselineContainer && baselineContainer.classList.contains('compact')) {
        element.style.top = `${sizes.top + (task.type !== this.instance.config.types.milestone ? (this.instance.config.bar_height + 2) : (this.instance.config.bar_height + 2))}px`;
      }

      return element;
    }

    return false;
  }

  async buildBaseline(baseline) {
    const { id: baselineId, gantt_id: projectId, event_id: eventId } = baseline;

    // This dispatch behaves like a getter, so we can use it inside ganttView context
    const projectBaselineData = await this.instance.ganttDataApi.getBaselineData({ projectId, eventId });

    if (+this.instance.config.gantt_id !== +projectBaselineData.tasks[0].gantt_id) {
      return;
    }

    this.instance.refreshData();

    const renderBaseline = this.addTaskLayer.bind(this, baseline);

    projectBaselineData.tasks.forEach(task => this.updateTask(baselineId, task, projectBaselineData));

    if (this.layersIds[baselineId]) {
      this.instance.removeTaskLayer(this.layersIds[baselineId]);
    }

    this.instance.config.row_height = this.instance.config.original_row_height * 2;
    this.helpers.addClass('.dhx_gantt_container', 'baseline-container');

    this.layersIds[baselineId] = this.instance.addTaskLayer({
      renderer: {
        render: renderBaseline,
        getRectangle(item, view) {
          return {
            top: view.getItemTop(item.id),
            height: view.getItemHeight(item.id),
            left: 0,
            right: Infinity,
          };
        },
      },
    });

    this.instance.config.baseline = Object.keys(this.layersIds).length;

    this.instance.render();
  }

  removeBaseline(baselineId) {
    const layerId = this.layersIds[baselineId];

    if (!layerId) return;

    this.instance.removeTaskLayer(layerId);

    this.instance.config.baseline--;

    if (this.instance.config.baseline < 1) {
      this.instance.config.row_height = this.instance.config.original_row_height;
      this.helpers.removeClass('.dhx_gantt_container', 'baseline-container');
    }

    delete this.layersIds[baselineId];

    this.instance.render();
  }
}

export default new BaselineChart();
